import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import TwoFactorSetup from '../misc/TwoFactorSetup.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './MyProfile.css';

const roleLabels = {
  super_admin: 'Beheerder',
  locatie_admin: 'Locatiebeheerder',
  docent: 'Docent',
  ouder: 'Ouder',
};

const updateNestedValue = (source, path, value) => {
  const keys = path.split('.');
  const root = Array.isArray(source) ? [...source] : { ...source };
  let target = root;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      target[key] = value;
      return;
    }

    const current = target[key];
    target[key] = Array.isArray(current) ? [...current] : { ...(current || {}) };
    target = target[key];
  });

  return root;
};

const ProfileField = ({ id, label, value, onChange, type = 'text', disabled = false, autoComplete }) => (
  <div className="mp-field">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      type={type}
      className="mp-control"
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      autoComplete={autoComplete}
    />
  </div>
);

const SectionTitle = ({ icon, title, description }) => (
  <div className="mp-section-heading">
    <span className="mp-section-icon"><i className={`bi ${icon}`} aria-hidden="true" /></span>
    <div><h2>{title}</h2>{description && <p>{description}</p>}</div>
  </div>
);

const MyProfile = () => {
  const { userInfo } = useAuth();
  const role = userInfo?.role || '';
  const token = sessionStorage.getItem('jwtToken');

  const [profile, setProfile] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user/getprofile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;

        const loadedProfile = response.data.profile;
        setProfile(loadedProfile);
        setTwoFactorEnabled(Boolean(loadedProfile.two_factor_enabled));
        setFormValues(role === 'locatie_admin'
          ? { location: loadedProfile.location || {}, contact: loadedProfile.contact || {} }
          : loadedProfile);
      } catch (requestError) {
        if (!active) return;
        if (requestError.response?.status === 403) {
          setError(requestError.response.data?.error || 'Je hebt geen toegang tot dit profiel.');
        } else {
          console.error('Fout bij ophalen profiel:', requestError);
          setError('Het profiel kon niet worden geladen. Probeer het opnieuw.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProfile();
    return () => { active = false; };
  }, [token, role]);

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const handleChange = (path, value) => {
    setFormValues((currentValues) => updateNestedValue(currentValues, path, value));
    setMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const payload = {};

      if (role === 'super_admin') {
        payload.customer = { address: formValues.customer?.address || '', phone: formValues.customer?.phone || '' };
        payload.contact = { firstname: formValues.contact?.firstname || '', lastname: formValues.contact?.lastname || '', mobile: formValues.contact?.mobile || '' };
      } else if (role === 'locatie_admin') {
        payload.location = { name: formValues.location?.name || '', address: formValues.location?.address || '', phone: formValues.location?.phone || '' };
        payload.contact = { firstname: formValues.contact?.firstname || '', lastname: formValues.contact?.lastname || '', mobile: formValues.contact?.mobile || '' };
      } else if (role === 'docent') {
        payload.street = formValues.address?.street || '';
        payload.housenr = formValues.address?.housenr || '';
        payload.postalcode = formValues.address?.postalcode || '';
        payload.city = formValues.address?.city || '';
        payload.mobile = formValues.contact?.mobile || '';
      } else if (role === 'ouder') {
        payload.parent = { mobile: formValues.parent?.mobile || '', email: formValues.parent?.email || '' };
        if (Array.isArray(formValues.children)) {
          payload.children = formValues.children.map((child) => ({
            student_id: child.student_id,
            address: { street: child.address?.street || '', housenr: child.address?.housenr || '', postalcode: child.address?.postalcode || '', city: child.address?.city || '' },
          }));
        }
      }

      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/user/updateprofile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.Status === 'Success') {
        setMessage('Je wijzigingen zijn succesvol opgeslagen.');
        setMessageType('success');
      }
    } catch (requestError) {
      console.error('Fout bij opslaan profiel:', requestError);
      setMessage(requestError.response?.data?.error || 'Het profiel kon niet worden opgeslagen.');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <main className="mp-page"><div className="mp-state" role="status"><span className="mp-loader" /><strong>Profiel laden</strong><span>Even geduld¦</span></div></main>;
  if (error) return <main className="mp-page"><div className="mp-state mp-state-error" role="alert"><span className="mp-state-icon"><i className="bi bi-exclamation-circle" /></span><strong>Profiel niet beschikbaar</strong><span>{error}</span></div></main>;

  return (
    <main className="mp-page">
      <div className="mp-shell">
        <header className="mp-heading">
          <div><span className="mp-eyebrow">Account</span><h1>Mijn gegevens</h1><p>Beheer je contactgegevens, adresinformatie en accountbeveiliging.</p></div>
          <span className="mp-role-badge"><i className="bi bi-person-check" />{roleLabels[role] || 'Gebruiker'}</span>
        </header>

        {message && <div className={`mp-notice mp-notice-${messageType}`} role="status"><i className={`bi ${messageType === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'}`} /><span>{message}</span><button type="button" onClick={() => setMessage('')} aria-label="Melding sluiten"><i className="bi bi-x-lg" /></button></div>}

        <div className="mp-layout">
          <div className="mp-main-column">
            {role === 'super_admin' && <section className="mp-card">
              <SectionTitle icon="bi-building" title="Organisatiegegevens" description="Algemene informatie en de primaire contactpersoon." />
              <div className="mp-card-body">
                <div className="mp-form-grid"><ProfileField id="customer-name" label="Naam organisatie" value={profile?.customer?.name} disabled /><ProfileField id="customer-address" label="Adres" value={formValues.customer?.address} onChange={(event) => handleChange('customer.address', event.target.value)} autoComplete="street-address" /><ProfileField id="customer-phone" label="Telefoon" value={formValues.customer?.phone} onChange={(event) => handleChange('customer.phone', event.target.value)} type="tel" autoComplete="tel" /></div>
                <div className="mp-divider" />
                <h3 className="mp-subheading">Contactpersoon</h3>
                <div className="mp-form-grid"><ProfileField id="contact-firstname" label="Voornaam" value={formValues.contact?.firstname} onChange={(event) => handleChange('contact.firstname', event.target.value)} autoComplete="given-name" /><ProfileField id="contact-lastname" label="Achternaam" value={formValues.contact?.lastname} onChange={(event) => handleChange('contact.lastname', event.target.value)} autoComplete="family-name" /><ProfileField id="contact-mobile" label="Mobiel" value={formValues.contact?.mobile} onChange={(event) => handleChange('contact.mobile', event.target.value)} type="tel" autoComplete="tel" /></div>
              </div>
            </section>}

            {role === 'locatie_admin' && <section className="mp-card">
              <SectionTitle icon="bi-geo-alt" title={formValues.location?.name || 'Locatiegegevens'} description="Beheer de gegevens van je locatie en contactpersoon." />
              <div className="mp-card-body">
                <div className="mp-form-grid"><ProfileField id="location-name" label="Naam locatie" value={formValues.location?.name} onChange={(event) => handleChange('location.name', event.target.value)} /><ProfileField id="location-address" label="Adres" value={formValues.location?.address} onChange={(event) => handleChange('location.address', event.target.value)} autoComplete="street-address" /><ProfileField id="location-phone" label="Telefoon" value={formValues.location?.phone} onChange={(event) => handleChange('location.phone', event.target.value)} type="tel" /></div>
                <div className="mp-divider" /><h3 className="mp-subheading">Contactpersoon</h3>
                <div className="mp-form-grid"><ProfileField id="location-contact-firstname" label="Voornaam" value={formValues.contact?.firstname} onChange={(event) => handleChange('contact.firstname', event.target.value)} /><ProfileField id="location-contact-lastname" label="Achternaam" value={formValues.contact?.lastname} onChange={(event) => handleChange('contact.lastname', event.target.value)} /><ProfileField id="location-contact-mobile" label="Mobiel" value={formValues.contact?.mobile} onChange={(event) => handleChange('contact.mobile', event.target.value)} type="tel" /><ProfileField id="location-contact-email" label="E-mailadres" value={formValues.contact?.email} type="email" disabled /></div>
              </div>
            </section>}

            {role === 'docent' && <section className="mp-card">
              <SectionTitle icon="bi-person-vcard" title={`${profile?.docent?.firstname || ''} ${profile?.docent?.lastname || ''}`.trim() || 'Persoonlijke gegevens'} description="Je adres- en contactgegevens als docent." />
              <div className="mp-card-body"><div className="mp-form-grid"><ProfileField id="teacher-street" label="Straat" value={formValues.address?.street} onChange={(event) => handleChange('address.street', event.target.value)} /><ProfileField id="teacher-housenr" label="Huisnummer" value={formValues.address?.housenr} onChange={(event) => handleChange('address.housenr', event.target.value)} /><ProfileField id="teacher-postalcode" label="Postcode" value={formValues.address?.postalcode} onChange={(event) => handleChange('address.postalcode', event.target.value)} /><ProfileField id="teacher-city" label="Plaats" value={formValues.address?.city} onChange={(event) => handleChange('address.city', event.target.value)} /><ProfileField id="teacher-mobile" label="Mobiel" value={formValues.contact?.mobile} onChange={(event) => handleChange('contact.mobile', event.target.value)} type="tel" /><ProfileField id="teacher-email" label="E-mailadres" value={formValues.contact?.email} type="email" disabled /></div></div>
            </section>}

            {role === 'ouder' && <section className="mp-card">
              <SectionTitle icon="bi-person" title={`${profile?.parent?.firstname || ''} ${profile?.parent?.lastname || ''}`.trim() || 'Contactgegevens'} description="Je contactgegevens en de adressen van gekoppelde kinderen." />
              <div className="mp-card-body">
                <div className="mp-form-grid"><ProfileField id="parent-mobile" label="Mobiel" value={formValues.parent?.mobile} onChange={(event) => handleChange('parent.mobile', event.target.value)} type="tel" /><div><ProfileField id="parent-email" label="E-mailadres" value={formValues.parent?.email} onChange={(event) => handleChange('parent.email', event.target.value)} type="email" /><div className="mp-inline-warning"><i className="bi bi-info-circle" /><span>Na een wijziging log je voortaan in met het nieuwe e-mailadres. Je wachtwoord blijft hetzelfde.</span></div></div></div>
                {Array.isArray(formValues.children) && formValues.children.length > 0 && <><div className="mp-divider" /><h3 className="mp-subheading">Kinderen</h3><div className="mp-children">{formValues.children.map((child, index) => <section className="mp-child-card" key={child.student_id}><div className="mp-child-heading"><span>{(child.firstname || '?')[0].toUpperCase()}</span><h4>{child.firstname} {child.lastname}</h4></div><div className="mp-form-grid"><ProfileField id={`child-${index}-street`} label="Straat" value={child.address?.street} onChange={(event) => handleChange(`children.${index}.address.street`, event.target.value)} /><ProfileField id={`child-${index}-housenr`} label="Huisnummer" value={child.address?.housenr} onChange={(event) => handleChange(`children.${index}.address.housenr`, event.target.value)} /><ProfileField id={`child-${index}-postalcode`} label="Postcode" value={child.address?.postalcode} onChange={(event) => handleChange(`children.${index}.address.postalcode`, event.target.value)} /><ProfileField id={`child-${index}-city`} label="Plaats" value={child.address?.city} onChange={(event) => handleChange(`children.${index}.address.city`, event.target.value)} /></div></section>)}</div></>}
              </div>
            </section>}

            <div className="mp-save-bar"><div><strong>Wijzigingen opslaan</strong><span>Controleer je gegevens voordat je ze opslaat.</span></div><button type="button" className="mp-save-button" onClick={handleSave} disabled={isSaving}>{isSaving ? <><span className="mp-button-spinner" />Opslaan¦</> : <><i className="bi bi-check2-circle" />Wijzigingen opslaan</>}</button></div>
          </div>

          <aside className="mp-security"><div className="mp-aside-heading"><span><i className="bi bi-shield-lock" /></span><div><h2>Beveiliging</h2><p>Bescherm je account met extra verificatie.</p></div></div><TwoFactorSetup twoFactorEnabled={twoFactorEnabled} onEnabled={() => setTwoFactorEnabled(true)} variant="card" /></aside>
        </div>
      </div>
    </main>
  );
};

export default MyProfile;